// Design-system kit — Slate & Amber. Buttons, cards, badges, toasts, tables, etc.
import React, { createContext, useContext, useRef, useState } from 'react';
import { FaCircleCheck, FaCircleInfo, FaTriangleExclamation, FaXmark } from 'react-icons/fa6';

// ── Toast ────────────────────────────────────────────────────────
const ToastContext = createContext({ toast: () => {} });
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(1);

  function toast(message, type = 'success') {
    const id = idRef.current++;
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }
  const styles = {
    success: 'bg-ink-900 text-white',
    error: 'bg-rose-600 text-white',
    info: 'bg-ink-800 text-white',
  };
  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed top-4 inset-x-0 z-[60] flex flex-col items-center gap-2 px-4 pointer-events-none">
        {toasts.map((t) => (
          <div key={t.id} className={`${styles[t.type]} rounded-xl px-4 py-3 text-sm font-medium shadow-pop flex items-center gap-2 max-w-sm w-full sm:w-auto`}>
            {t.type === 'success' && <FaCircleCheck className="text-emerald-400 shrink-0" />}
            {t.type === 'error' && <FaTriangleExclamation className="text-amber-300 shrink-0" />}
            {t.type === 'info' && <FaCircleInfo className="text-sky-300 shrink-0" />}
            <span>{t.message}</span>
            <button onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))} className="ml-1 opacity-60 hover:opacity-100"><FaXmark /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
export function useToast() { return useContext(ToastContext); }

// ── Page header ─────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-ink-900">{title}</h1>
        {subtitle && <p className="text-sm text-ink-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ── Stat card ───────────────────────────────────────────────────
export function StatCard({ icon, label, value, sub, tone = 'amber' }) {
  const tones = {
    amber: 'bg-brand-50 text-brand-600',
    emerald: 'bg-accent-50 text-accent-600',
    rose: 'bg-rose-50 text-rose-600',
    sky: 'bg-sky-50 text-sky-600',
    slate: 'bg-ink-100 text-ink-600',
  };
  return (
    <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-4 sm:p-5">
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-base ${tones[tone] || tones.amber}`}>{icon}</div>
      </div>
      <div className="text-2xl font-bold text-ink-900 mt-3">{value}</div>
      <div className="text-xs font-semibold text-ink-400 uppercase tracking-wide mt-0.5">{label}</div>
      {sub && <div className="text-xs text-ink-500 mt-1.5">{sub}</div>}
    </div>
  );
}

// ── Badge ───────────────────────────────────────────────────────
export function Badge({ children, color = 'ink' }) {
  const map = {
    present:  'bg-emerald-50 text-emerald-700',
    absent:   'bg-rose-50 text-rose-700',
    leave:    'bg-amber-50 text-amber-700',
    pending:  'bg-amber-50 text-amber-700',
    paid:     'bg-emerald-50 text-emerald-700',
    partial:  'bg-amber-50 text-amber-700',
    approved: 'bg-emerald-50 text-emerald-700',
    rejected: 'bg-rose-50 text-rose-700',
    done:     'bg-sky-50 text-sky-700',
    active:   'bg-emerald-50 text-emerald-700',
    completed:'bg-ink-100 text-ink-600',
    paused:   'bg-amber-50 text-amber-700',
    ink:      'bg-ink-100 text-ink-600',
  };
  return <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-md ${map[color] || map.ink}`}>{children}</span>;
}

// ── Avatar ──────────────────────────────────────────────────────
const avatarTones = ['bg-brand-500', 'bg-sky-500', 'bg-accent-600', 'bg-violet-500', 'bg-rose-500', 'bg-ink-700'];
export function Avatar({ name, className = 'w-9 h-9 text-xs' }) {
  const initials = (name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  const tone = avatarTones[((name || '').length || 0) % avatarTones.length];
  return <div className={`rounded-full ${tone} text-white flex items-center justify-center font-bold shrink-0 ${className}`}>{initials}</div>;
}

// ── Empty state ─────────────────────────────────────────────────
export function EmptyState({ icon = '📭', title, sub }) {
  return (
    <div className="text-center py-14">
      <div className="text-3xl mb-2">{icon}</div>
      <div className="font-semibold text-ink-700">{title}</div>
      {sub && <div className="text-sm text-ink-400 mt-1">{sub}</div>}
    </div>
  );
}

// ── Skeleton loading ────────────────────────────────────────────
export function Skeleton({ className = 'h-4' }) {
  return <div className={`rounded-md bg-ink-100 animate-pulse ${className}`} />;
}

// ── Modal (bottom sheet on mobile) ──────────────────────────────
export function Modal({ open, onClose, title, children, footer, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-ink-900/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className={`relative bg-white w-full sm:rounded-xl rounded-t-2xl shadow-card max-h-[92vh] overflow-y-auto pb-safe ${wide ? 'sm:max-w-2xl' : 'sm:max-w-md'}`}>
        <div className="sticky top-0 bg-white/95 backdrop-blur flex items-center justify-between px-5 py-4 border-b border-ink-100 z-10">
          <h3 className="font-semibold text-ink-900 text-base">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-400 transition"><FaXmark /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="px-5 pb-5">{footer}</div>}
      </div>
    </div>
  );
}

// ── Confirm dialog ──────────────────────────────────────────────
export function Confirm({ open, onClose, onConfirm, title = 'নিশ্চিত করুন?', message = 'এই কাজটি ফেরত নেওয়া যাবে না।', confirmText = 'হ্যাঁ', danger = true }) {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="text-center py-2">
        <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center text-2xl ${danger ? 'bg-rose-50 text-rose-500' : 'bg-brand-50 text-brand-600'}`}>
          <FaTriangleExclamation />
        </div>
        <p className="text-sm text-ink-600 mt-3">{message}</p>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="btn-ghost flex-1">বাতিল</button>
          <button onClick={() => { onConfirm(); onClose(); }} className={`flex-1 btn ${danger ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'btn-primary'}`}>{danger ? 'হ্যাঁ, মুছুন' : confirmText}</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Status segmented pills (marking present/absent/leave) ───────
export function StatusPills({ value, onChange, options = ['present', 'absent', 'leave'] }) {
  const styles = {
    present: { on: 'bg-emerald-600 text-white border-emerald-600', off: 'bg-white text-ink-500 border-ink-200' },
    absent:  { on: 'bg-rose-600 text-white border-rose-600', off: 'bg-white text-ink-500 border-ink-200' },
    leave:   { on: 'bg-amber-500 text-white border-amber-500', off: 'bg-white text-ink-500 border-ink-200' },
  };
  return (
    <div className="flex gap-1.5">
      {options.map((o) => (
        <button type="button" key={o}
          onClick={() => onChange(o)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${value === o ? styles[o].on : styles[o].off}`}>
          {o === 'present' ? 'উপস্থিত' : o === 'absent' ? 'অনুপস্থিত' : 'ছুটি'}
        </button>
      ))}
    </div>
  );
}